import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import './Profile.css';

interface UserProfile {
  id: number;
  email: string;
  username?: string;
  class_name?: string;
  is_admin?: boolean;
}

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [username, setUsername] = useState('');
  const [class_name, setClass_name] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const response = await api.get('/user/profile');
      const userProfile = response.data;
      setProfile(userProfile);
      setUsername(userProfile.username || '');
      setClass_name(userProfile.class_name || '');
      // 更新localStorage中的isAdmin状态
      if (userProfile.is_admin) {
        localStorage.setItem('isAdmin', 'true');
      } else {
        localStorage.removeItem('isAdmin');
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || '加载失败' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      const response = await api.put('/user/profile', {
        username: username.trim() || null,
        class_name: class_name.trim() || null,
      });

      setProfile(response.data.user);
      setMessage({ type: 'success', text: '保存成功！' });
      
      // 3秒后清除消息
      setTimeout(() => {
        setMessage(null);
      }, 3000);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || '保存失败' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-container">
        <div className="profile-card">
          <p>加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <h1 className="page-title">我的</h1>
      
      <div className="profile-card">
        <div className="profile-header">
          <div className="profile-avatar">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#FFD700" strokeWidth="2.5">
              <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
          </div>
          <h2 className="profile-email">{profile?.email}</h2>
        </div>

        {message && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="profile-form">
          <div className="form-group">
            <label htmlFor="username">用户名</label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="input"
              placeholder="请输入用户名"
              maxLength={100}
            />
            <small className="form-hint">用于显示在作品和弹幕中</small>
          </div>

          <div className="form-group">
            <label htmlFor="class_name">班级</label>
            <input
              id="class_name"
              type="text"
              value={class_name}
              onChange={(e) => setClass_name(e.target.value)}
              className="input"
              placeholder="请输入班级"
              maxLength={100}
            />
            <small className="form-hint">用于作品提交时自动填充</small>
          </div>

          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? '保存中...' : '保存'}
          </button>
        </form>

        <div className="profile-actions">
          <button onClick={handleLogout} className="btn btn-logout-profile">
            退出登录
          </button>
        </div>
      </div>
    </div>
  );
};

export default Profile;

